import { Select, SelectContent } from "@/components/ui/select"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import * as SelectPrimitive from "@radix-ui/react-select"
import type { ReactNode } from "react"
import type { Verdict } from "../../studio/studioTypes"
import {
  SETTABLE_VERDICTS,
  verdictLabelKey,
  verdictVisual,
} from "../mapping/verdictDisplay"
import { ExplainedSelectItem } from "./ExplainedSelectItem"

/** Per-option explainer, the muted second line of each `ExplainedSelectItem`
 *  (never a native `title`) — the five verdict labels alone don't say what
 *  setting one actually asserts. */
const VERDICT_EXPLAINER_KEY: Record<Verdict, TranslationKeys> = {
  compliant: "MC_DECL_V2_VERDICT_COMPLIANT_EXPLAINER",
  compliant_with_conditions:
    "MC_DECL_V2_VERDICT_COMPLIANT_WITH_CONDITIONS_EXPLAINER",
  non_compliant: "MC_DECL_V2_VERDICT_NON_COMPLIANT_EXPLAINER",
  pending: "MC_DECL_V2_VERDICT_PENDING_EXPLAINER",
  unknown: "MC_DECL_V2_VERDICT_UNKNOWN_EXPLAINER",
}

export interface VerdictSelectProps {
  value: Verdict | null
  onChange: (v: Verdict) => void
  disabled?: boolean
  /** A verdict edit on this card is in flight (TUR-1211, W5-5): the
   *  optimistic patch already shows the new value, this is the quiet
   *  saving state on the control itself. */
  "aria-busy"?: boolean
  /** Sentence-form label for the trigger ("konform mit Ausnahme" as the
   *  predicate of the card's header sentence, `HeaderSentence.tsx`); the
   *  popup keeps the full labels. Omitted: the plain catalog label. */
  triggerLabel?: ReactNode
}

/** The tint class for a verdict — a thin alias over the shared visual map, so
 *  this control and the navigator dot cannot drift apart. */
export function verdictSv2Class(verdict: Verdict | null): string {
  return verdictVisual(verdict).tint
}

/**
 * The per-unit verdict control.
 *
 * WAS A NATIVE `<select>` (v1 precedent: the remap picker in StudioScreenB
 * and the reference-list editor in StudioScreenA are both plain `<select>`s;
 * there was no shadcn `<Select>` usage anywhere under review/studio/). The
 * product owner flagged it with a screenshot: opening a native select pops
 * the OS's own light-mode menu on top of this dark UI — jarring, and the one
 * place in Studio V2 that visibly breaks the app's chrome. This now wraps
 * `@/components/ui/select` (Radix), the app's actual standard for a
 * value-picker on a dark surface (`ConformityGenerateModule`,
 * `CreateCustomerModal`).
 *
 * TRIGGER VS CONTENT. Radix's `SelectContent` portals to `document.body` by
 * default — fine here, unlike `RemapPopover`'s/`SupplierAssignControl`'s
 * `disablePortal` popovers, because the dropdown is styled with `ui/select`'s
 * own GLOBAL tokens (`bg-popover`/`text-popover-foreground`), which resolve
 * correctly outside `.sv2-root` too. Only `.sv2-root`-scoped CSS variables
 * need `disablePortal`; this control never uses one. The TRIGGER, on the
 * other hand, keeps this file's own `sv2-vsel`/`sv2-exv` pill classes
 * (bypassing `ui/select`'s `SelectTrigger`, which hard-codes a light-agnostic
 * layout and its own chevron `.sv2-vsel` already draws one via
 * `background-image`) — same tint (`verdictSv2Class`), same shape as before.
 */
export function VerdictSelect({
  value,
  onChange,
  disabled,
  "aria-busy": ariaBusy,
  triggerLabel,
}: VerdictSelectProps) {
  const { translate } = useTranslation()
  const visibleLabel =
    triggerLabel ??
    (value
      ? translate(verdictLabelKey(value))
      : translate("MC_DECL_V2_VERDICT_PENDING"))
  return (
    <Select
      value={value ?? undefined}
      onValueChange={(v) => onChange(v as Verdict)}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger
        aria-label={translate("MC_DECL_V2_VERDICT_SELECT_ARIA")}
        aria-busy={ariaBusy}
        // W5 overflow fix: at narrow widths `.sv2-vsel` still ellipsizes
        // (`.sv2-root .sv2-hsent .sv2-vsel`, studioV2.css); this `title`
        // keeps the full text reachable (native tooltip) even when it's
        // visually cut, same visible text so it can never disagree with it.
        title={typeof visibleLabel === "string" ? visibleLabel : undefined}
        className={cn("sv2-vsel sv2-exv", verdictSv2Class(value))}
      >
        {visibleLabel}
      </SelectPrimitive.Trigger>
      <SelectContent>
        {SETTABLE_VERDICTS.map((v) => (
          <ExplainedSelectItem
            key={v}
            value={v}
            label={translate(verdictLabelKey(v))}
            explainer={translate(VERDICT_EXPLAINER_KEY[v])}
          />
        ))}
      </SelectContent>
    </Select>
  )
}
